import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthTokens } from '../types/api';
import { apiClient } from '../api/client';
import { env } from '../config/environment';
import { useQueryClient } from '@tanstack/react-query';
import { webSocketManager } from '../services/websocket';

interface AuthContextType {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (tokens: AuthTokens, user: User) => void;
  logout: () => void;
  updateUser: (user: User) => void;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  const isAuthenticated = Boolean(user && tokens);

  useEffect(() => {
    // Load auth state from localStorage on mount
    loadAuthState();
  }, []);

  const loadAuthState = async () => {
    try {
      const savedTokens = localStorage.getItem('auth_tokens');
      const savedUser = localStorage.getItem('user_profile');

      if (savedTokens && savedUser) {
        const parsedTokens: AuthTokens = JSON.parse(savedTokens);
        const parsedUser: User = JSON.parse(savedUser);

        setTokens(parsedTokens);
        setUser(parsedUser);
        apiClient.setAuthToken(parsedTokens.accessToken);

        // Try to refresh user profile to ensure it's up to date
        try {
          // Only attempt refresh if we actually have a refresh token
          if (parsedTokens.refreshToken) {
            await refreshAuth();
          }
        } catch (error) {
          console.warn('Failed to refresh auth on load:', error);
          // If refresh fails, we'll continue with cached data
        }
      }
    } catch (error) {
      console.error('Failed to load auth state:', error);
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  const login = (newTokens: AuthTokens, newUser: User) => {
    setTokens(newTokens);
    setUser(newUser);
    apiClient.setAuthToken(newTokens.accessToken);

    // Save to localStorage
    try {
      localStorage.setItem('auth_tokens', JSON.stringify(newTokens));
      localStorage.setItem('user_profile', JSON.stringify(newUser));
    } catch (error) {
      console.warn('Failed to save auth state:', error);
    }
  };

  const logout = () => {
    setTokens(null);
    setUser(null);
    apiClient.clearAuthToken();
    webSocketManager.disconnect();

    // Clear localStorage
    try {
      localStorage.removeItem('auth_tokens');
      localStorage.removeItem('user_profile');
    } catch (error) {
      console.warn('Failed to clear auth state:', error);
    }
  };

  // WebSocket connection management
  useEffect(() => {
    if (isAuthenticated && tokens?.idToken) {
      // Connect WebSocket when authenticated
      webSocketManager.connect(tokens.idToken).catch((error) => {
        console.error('Failed to connect WebSocket:', error);
      });
    } else {
      // Disconnect WebSocket when not authenticated
      webSocketManager.disconnect();
    }
  }, [isAuthenticated, tokens?.idToken]);

  // WebSocket message handler for credit updates
  useEffect(() => {
    const unsubscribe = webSocketManager.onMessage('credits.updated', (data: { balance: number }) => {
      console.log('[AuthContext] Received credit update:', data);
      
      if (user && typeof data.balance === 'number') {
        const updated = { ...user, creditsBalance: data.balance } as User;
        setUser(updated);
        try {
          localStorage.setItem('user_profile', JSON.stringify(updated));
        } catch (error) {
          console.warn('Failed to save updated user profile:', error);
        }
      }
      
      // Refresh any credits queries
      queryClient.invalidateQueries({ queryKey: ['credits'] });
    });

    return unsubscribe;
  }, [user, queryClient]);

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    try {
      localStorage.setItem('user_profile', JSON.stringify(updatedUser));
    } catch (error) {
      console.warn('Failed to save updated user profile:', error);
    }
  };

  const refreshAuth = async () => {
    // If no refresh token is present (e.g., legacy localStorage), skip silently
    if (!tokens?.refreshToken) {
      return;
    }

    try {
      // Call refresh endpoint
      const response = await apiClient.post<{
        success: boolean;
        data: {
          accessToken: string;
          idToken: string;
        };
      }>('/auth/refresh', {
        refreshToken: tokens.refreshToken,
        // Provide username (email) so backend can compute Cognito SECRET_HASH when needed
        username: user?.email,
      });

      if (response.success && response.data) {
        const newTokens: AuthTokens = {
          ...tokens,
          accessToken: response.data.accessToken,
          idToken: response.data.idToken,
        };

        setTokens(newTokens);
        apiClient.setAuthToken(newTokens.accessToken);

        try {
          localStorage.setItem('auth_tokens', JSON.stringify(newTokens));
        } catch (error) {
          console.warn('Failed to save refreshed tokens:', error);
        }

        // Get updated user profile
        console.log('🔍 [AuthContext] Fetching user profile from /auth/me...');
        const profileResponse = await apiClient.get<{
          success: boolean;
          data: User;
        }>('/auth/me');
        
        console.log('🔍 [AuthContext] Profile response:', profileResponse);
        console.log('🔍 [AuthContext] User credits from /auth/me:', profileResponse.data?.creditsBalance);

        if (profileResponse.success && profileResponse.data) {
          updateUser(profileResponse.data);
        }
      }
    } catch (error: any) {
      console.error('Failed to refresh auth:', error);
      // Only force logout on explicit unauthorized errors; otherwise keep cached tokens
      if (error?.name === 'UnauthorizedError' || error?.statusCode === 401) {
        logout();
      }
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    tokens,
    isAuthenticated,
    isLoading,
    login,
    logout,
    updateUser,
    refreshAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
